import { c as _c } from "react/compiler-runtime";
import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { type ApiInputToken, InterfaceChainUtils, ONE, parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import { formatUnits } from 'viem';
import { getMarketUrl, getVaultUrl } from '@/helpers/routing';
import type { AllocationAdapter, AllocationTransaction } from '../allocationTransactions.contract';
import type { AllocationTarget, VaultV2AllocationTransactionsTableRow } from '../config/VaultV2AllocationTransactions.types';
export interface AllocationTransactionRows {
  items?: readonly AllocationTransaction[];
  chainId: ChainId;
}
type AllocationToken = NonNullable<AllocationTransaction['asset']>;
function toInputToken({
  address,
  symbol,
  decimals,
  logoUri
}: AllocationToken, chainId: ChainId, priceUsd: number | null = null): ApiInputToken {
  return {
    address,
    symbol: symbol ?? '',
    name: symbol ?? '',
    decimals,
    logoURI: logoUri,
    chain: {
      id: chainId
    },
    priceUsd
  };
}

/** The asset's USD price at the event, so the amount gets the digits its price calls for. */
function toUnitPriceUsd(assetsUsd: number | null, absAssets: bigint, decimals: number): number | null {
  if (assetsUsd == null || absAssets === 0n) return null;
  const price = Math.abs(assetsUsd) / Number(formatUnits(absAssets, decimals));
  // `parseApiAsset` cannot read a price from 1e21 up, which `toFixed` writes in exponent form.
  return Number.isFinite(price) && price > 0 && price < 1e21 ? price : null;
}
function buildTarget(adapter: AllocationAdapter, chainId: ChainId): AllocationTarget {
  const identifier = InterfaceChainUtils.getIdentifierById(chainId);
  switch (adapter.kind) {
    case 'vaultV1':
    case 'vaultV2':
      {
        const {
          address,
          name
        } = adapter.vault;
        return {
          kind: 'vault',
          address,
          name,
          isVaultV2: adapter.kind === 'vaultV2',
          // Unnamed vaults stay navigable via the address-only route, which
          // resolves the vault and redirects to its canonical slug.
          url: name ? getVaultUrl({
            identifier,
            vaultAddress: address,
            vaultName: name
          }) : `/${identifier}/vault/${address}`
        };
      }
    case 'marketV1':
      {
        const markets = adapter.markets.map(({
          marketId,
          market
        }) => {
          if (!market) return undefined;
          const collateralSymbol = market.collateralAsset?.symbol;
          const loanSymbol = market.loanAsset.symbol;
          return {
            marketId,
            collateralAsset: market.collateralAsset && toInputToken(market.collateralAsset, chainId),
            loanAsset: toInputToken(market.loanAsset, chainId),
            lltv: BigInt(market.lltv),
            url: collateralSymbol && loanSymbol ? getMarketUrl({
              identifier,
              marketId,
              collateralSymbol,
              loanSymbol
            }) : undefined
          };
        }).filter(isDefined);

        // Markets Core could not resolve still count toward the event: they
        // surface in the "+N" tally by id rather than being silently dropped.
        const unresolvedMarketIds = adapter.markets.filter(({
          market
        }) => !market).map(({
          marketId
        }) => marketId);
        if (markets.length === 0 && unresolvedMarketIds.length === 0) {
          return {
            kind: 'unknown',
            address: adapter.address,
            url: ChainUtils.getExplorerAddressUrl(chainId, adapter.address)
          };
        }
        return {
          kind: 'markets',
          markets,
          unresolvedMarketIds
        };
      }
    case 'unknown':
      return {
        kind: 'unknown',
        address: adapter.address,
        url: ChainUtils.getExplorerAddressUrl(chainId, adapter.address)
      };
  }
}
export const useAllocationTransactionsRow = t0 => {
  const $ = _c(6);
  const {
    items,
    chainId
  } = t0;
  let t1;
  bb0: {
    if (!items) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== chainId || $[2] !== items) {
      let t3;
      if ($[4] !== chainId) {
        t3 = tx => {
          const sign = tx.type === "deallocate" ? -ONE : ONE;
          const absAssets = BigInt(tx.assets);
          const token = tx.asset ? parseApiAsset(toInputToken(tx.asset, chainId, toUnitPriceUsd(tx.assetsUsd, absAssets, tx.asset.decimals))) : null;
          const usdValue = tx.assetsUsd != null ? safeParseNumber(tx.assetsUsd) : null;
          return {
            chainId,
            timestamp: BigInt(tx.timestamp),
            typeText: tx.type === "deallocate" ? "Deallocate" : "Allocate",
            sender: tx.sender,
            urlSender: ChainUtils.getExplorerAddressUrl(chainId, tx.sender),
            assets: absAssets * sign,
            assetsUsd: usdValue != null ? usdValue * sign : null,
            token,
            target: buildTarget(tx.adapter, chainId),
            hash: tx.txHash,
            urlHash: ChainUtils.getExplorerTransactionUrl(chainId, tx.txHash)
          };
        };
        $[4] = chainId;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      t2 = items.map(t3);
      $[1] = chainId;
      $[2] = items;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  return t1;
};